import { Component, HostBinding, inject, Input, OnInit } from '@angular/core';
import { LorenzFormGroup } from '../../lorenz-form-group';
import { LorenzFormService } from '../../lorenz-form.service';
import { LorenzControlTypeEnum } from '../../enums/lorenz-control-type.enum';
import { LorenzFormList } from '../../lorenz-form-list';
import { LorenzFormControl } from '../../lorenz-form-control';
import { LorenzFormComponentDisplayOptions } from '../../interfaces/lorenz-form-component-options.interface';
import { LorenzFormLayoutEnum } from '../../enums/lorenz-form-layout.enum';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { LorenzFormListComponent } from '../lorenz-form-list/lorenz-form-list.component';
import { LorenzFormControlComponent } from '../lorenz-form-control/lorenz-form-control.component';
import { MatButtonModule, MatIconButton } from '@angular/material/button';
import { LibFormValidityDotComponent } from '../validity-dot/lib-form-validity-dot.component';
import { TranslateModule } from '@ngx-translate/core';
import { MatIcon } from '@angular/material/icon';
import { LibFormControlCheckboxComponent } from '../../controls/checkbox/lib-form-control-checkbox.component';
import { AppIdEnum } from '@lib/shared';

@Component({
	selector: 'lib-form-component-group',
	// prettier-ignore
	imports: [
		TranslateModule,
		NgTemplateOutlet,
		LorenzFormListComponent,
		LorenzFormControlComponent,
		NgClass,
		LibFormValidityDotComponent,
		MatButtonModule,
		MatIconButton,
		MatIcon,
		LibFormControlCheckboxComponent
	],
	templateUrl: './lorenz-form-group.component.html',
	styleUrl: './lorenz-form-group.component.scss'
})
export class LorenzFormGroupComponent implements OnInit {
	protected readonly LorenzControlTypeEnum = LorenzControlTypeEnum;

	private readonly lorenzFormService = inject(LorenzFormService);

	private _lorenzFormGroup: LorenzFormGroup | null = null;

	private _lorenzFormList: LorenzFormList | null = null;

	get lorenzFormGroup(): LorenzFormGroup | null {
		return this._lorenzFormGroup;
	}

	@Input({ required: false, alias: 'group' })
	set lorenzFormGroup(value: LorenzFormGroup | null) {
		this._lorenzFormGroup = value;
	}

	@Input({ required: false, alias: 'options' }) lorenzFormComponentOptions: LorenzFormComponentDisplayOptions = {};

	@HostBinding('attr.id') id: string | null = null;

	@HostBinding('class') hostCssClass: string | null = null;

	@HostBinding('style.display') get displayStyle(): string {
		const isVisible = this.lorenzFormGroup?.isVisible();
		const completlyHiddenWhileDisabled = this.lorenzFormGroup?.getCompletlyHiddenWhileDisabled() !== true;
		return isVisible || completlyHiddenWhileDisabled ? '' : 'none';
	}

	lorenzFormChildren: (LorenzFormGroup | LorenzFormList | LorenzFormControl)[] = [];

	expandCheckboxControl: LorenzFormControl | null = null;

	get isExpandable(): boolean {
		return this.lorenzFormGroup?.isExpandable() ?? false;
	}

	get isListItem(): boolean {
		return this.lorenzFormGroup?.getControlType() === LorenzControlTypeEnum.LISTITEM;
	}

	get isLayoutFloatingLabels(): boolean {
		return this.lorenzFormComponentOptions?.layout === LorenzFormLayoutEnum.FLOATING_LABELS;
	}

	get hasActionButton(): boolean {
		const result = this.hasDeleteButton || this.hasSaveButton;
		// console.log('lorenz-form-group.component.hasActionButton()', this.lorenzFormGroup, this.lorenzFormGroup?.payload, result);
		return result;
	}

	get hasDeleteButton(): boolean {
		return this._lorenzFormList?.hasDeleteButton() ?? false;
	}

	get hasGroupHeaders(): boolean {
		if (this.lorenzFormGroup?.parent === this.lorenzFormGroup?.getRoot()) {
			return this.lorenzFormComponentOptions?.displayGroupHeaders ?? true;
		}
		return true;
	}

	get hasSaveButton(): boolean {
		const hasSaveButton = this.lorenzFormGroup?.hasSaveButton() ?? false;
		const isUnlocked = this.lorenzFormGroup?.isUnlocked() ?? true;
		const hasSaveableStatus = ['NEW', 'REVIEW'].includes(this.lorenzFormGroup?.getStatus() ?? '');
		return hasSaveButton && isUnlocked && hasSaveableStatus;
	}

	get showPartnerSaveTextButton(): boolean {
		return this.lorenzFormGroup?.getAppId() === AppIdEnum.CONTRACT;
	}

	get addButtonDisabled(): boolean {
		const help = this._lorenzFormList && this._lorenzFormList.valid;
		console.log('this._lorenzFormList', this._lorenzFormList);
		return !help;
	}

	get deleteButtonDisabled(): boolean {
		const help = this.lorenzFormGroup?.getControlType() === LorenzControlTypeEnum.LISTITEM && Object.keys(this.lorenzFormGroup.parent?.controls ?? {}).length > 1;
		return !help;
	}

	get saveButtonDisabled(): boolean {
		return this.lorenzFormGroup?.getSaveButtonDisabled() ?? true;
		// const hasSaveFunction = !!this.lorenzFormGroup?.getSaveButtonFn();
		// return !hasSaveFunction || (this.lorenzFormGroup?.getSaveButtonDisabled() ?? false);
	}

	public ngOnInit(): void {
		if (this.lorenzFormGroup) {
			this.id = this.lorenzFormGroup.getFullKey() === '' ? 'form-root' : this.lorenzFormGroup.getFullKey();

			this.updateHostCssClass();

			this.lorenzFormChildren = this.lorenzFormService.getChildren(this.lorenzFormGroup);

			this.expandCheckboxControl = this.lorenzFormGroup?.getControl('checked');

			if (this.lorenzFormGroup.getControlType() === LorenzControlTypeEnum.LISTITEM) {
				this._lorenzFormList = this.lorenzFormGroup.parent as LorenzFormList;
			}

			if (this.lorenzFormGroup.isFirstLevel()) {
				this.lorenzFormGroup.valueChanges.subscribe(() => setTimeout(() => this.updateHostCssClass(), 250)); // TODO
				this.lorenzFormGroup.statusChanges.subscribe(() => setTimeout(() => this.updateHostCssClass(), 250)); // TODO
			}
		}
	}

	public deleteListItem() {
		const lorenzFormList = this.lorenzFormGroup?.getList();
		if (this.lorenzFormGroup && lorenzFormList) {
			lorenzFormList.removeItem(this.lorenzFormGroup);
		}
	}

	public getListSuffix(): string {
		if (this.lorenzFormGroup && this.lorenzFormGroup.getControlType() === LorenzControlTypeEnum.LISTITEM) {
			const parent = this.lorenzFormGroup.parent as LorenzFormGroup;
			if (parent) {
				const prefix = parent.payload?.listItemPrefix ?? '';
				const keys = Object.keys(parent.controls);
				const index = keys.indexOf(this.lorenzFormGroup.getKey());
				const label = (prefix + ' #' + (index + 1)).trim();
				return ' <span class="pos">' + label + '</span>';
			}
		}
		return '';
	}

	public callStoreRequestFn(): void {
		const storeFn = this.lorenzFormGroup?.getSaveButtonFn() ?? null;
		if (storeFn) {
			storeFn(this.lorenzFormGroup?.getRoot());
		}
	}

	private updateHostCssClass(): void {
		if (this.lorenzFormGroup) {
			// prettier-ignore
			this.hostCssClass = [
				this.isListItem ? 'list-item' : null,
				'group',
				this.lorenzFormComponentOptions?.layout ?? this.lorenzFormGroup.getLayout(),
				this.lorenzFormGroup.getKey(),
				this.lorenzFormGroup.valid ? 'border-valid' : 'border-invalid',
				this.lorenzFormGroup.isEnabled() ? 'enabled' : 'disabled'
			].join(' ').trim();

			// console.log('updateHostCssClass', this.lorenzFormGroup.valid, this.lorenzFormGroup);
		}
	}
}
